import React, { useState } from 'react';
import type { FieldArrayRenderProps, FormikTouched, FormikValues } from 'formik';
import { FieldArray, getIn, setNestedObjectValues, useFormikContext } from 'formik';
import { get, isEmpty, isEqual, isNil, snakeCase, trim } from 'lodash';

import CheckboxFormik from '~/components/CheckboxFormik';
import { Text } from '~/components/core';
import ArrayMultiselectFieldWithChipsFormik from '~/components/core/ArrayMultiselectFieldWithChipsFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import InnerCard from '~/components/core/Cards/InnerCard';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import type { DndDragHandleProps } from '~/components/core/DndOrderedList/DndOrderedList.types';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import StatusChipCore from '~/components/core/StatusChipCore';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import WarningCircleIcon from '~/components/icons/WarningCircle';
import TextFieldFormik from '~/components/TextFieldFormik';
import type {
  FieldGroup,
  FieldGroupSource,
  FieldGroupType,
  HierarchySubField,
} from '~/components/types/incident-configuration-types';

import ReorderDragHandle from '../components/ReorderDragHandle';
import copies from '../copies.json';
import HiddenIndication from '../HiddenIndication/HiddenIndication';
import { isNewRow, markNewRow } from '../newRowIdentity';
import { buildCategoryLimitOptions, usePolicyCategoryOptions } from '../PolicyCategoriesOptions';
import { reorderArrayField } from '../reorderArrayField';

import FieldGroupSourceDialog from './FieldGroupSourceDialog';
import FieldGroupSourceSummary from './FieldGroupSourceSummary';

const TEXTS = copies.field_groups;

/** Groups live beside the flat configured fields of incident details, and are placed by `section` as they are. */
export const FIELD_GROUPS_PATH = 'configuration.incident_details.field_groups';

// Mirrors MAX_FIELD_GROUPS in server/managers/incident_configuration_manager.py and
// MIN/MAX_SUB_FIELDS_PER_GROUP in server/managers/product_config/schemas/field_group_schemas.py.
// The backend rejects a save past either, so the affordance is disabled here rather than letting
// the admin discover the cap through a failed save.
export const MAX_FIELD_GROUPS = 10;
const MIN_SUB_FIELDS_PER_GROUP = 1;
const MAX_SUB_FIELDS_PER_GROUP = 5;

// Typed against the union rather than listed loose: a second FieldGroupType with no label here
// fails `tsc` instead of silently dropping out of the Type select.
const FIELD_GROUP_TYPE_LABELS: Record<FieldGroupType, string> = {
  hierarchy_api: 'Hierarchy API',
};

const newSubField = (level: number): HierarchySubField => markNewRow({ id: `level_${level}`, desc: `Level ${level}` });

export const getNewFieldGroup = (index: number, section?: string): FieldGroup =>
  markNewRow({
    id: `field_group_${index}`,
    desc: `Field Group ${index}`,
    type: 'hierarchy_api',
    active: true,
    mandatory: false,
    section,
    // Exactly one of `source_key` / `static_json` must be set; a new group starts key-backed and can
    // be switched to a static tree while it is still unsaved. `url` and `auth_token` ride alongside
    // the key on this route only -- they belong to the source row the key names, not to the
    // configuration (see `FieldGroupSource`).
    source: { source_key: '', url: '', auth_token: '' },
    sub_fields: [newSubField(1)],
  });

/**
 * The identity rule ConfiguredFieldsSection's `isRemovable` uses, for groups and for sub-fields
 * alike: a row may still be removed only while it is unsaved, and a row added in this session stays
 * removable even after its key grows into a saved row's key.
 *
 * Removal is what this gates because the shared incident-configuration diff guard refuses ANY
 * removal from a saved configuration. A saved *group* has a way out - it is retired with
 * `active: false`. A saved *level* has none: `HierarchySubField` declares no `active`, so a group's
 * depth is fixed once saved, and a group whose depth has to change is retired and replaced. Levels
 * may still be added to a saved group; only removal is closed.
 */
export const isUnsavedRow = (row: { id?: string } | undefined, savedRows: { id?: string }[] | undefined): boolean =>
  isNewRow(row) || !savedRows?.some((saved) => saved?.id === row?.id);

interface FieldGroupConfigurationCardProps {
  /** Position in the `field_groups` array; the Formik path is derived from it. */
  index: number;
  /** Row controls rendered in the card header, as the sibling field card takes them. */
  actions?: React.ReactNode;
  disabled?: boolean;
}

const FieldGroupConfigurationCard: React.FC<FieldGroupConfigurationCardProps> = ({
  index,
  actions = undefined,
  disabled = false,
}) => {
  const { values, initialValues, touched, submitCount, setFieldValue, setFieldTouched } =
    useFormikContext<FormikValues>();
  const policyCategoryOptions = usePolicyCategoryOptions();
  const [isSourceDialogOpen, setIsSourceDialogOpen] = useState(false);

  const fieldId = `${FIELD_GROUPS_PATH}[${index}]`;
  const fieldGroup: Partial<FieldGroup> = get(values, fieldId, {});
  const savedFieldGroups: FieldGroup[] = get(initialValues, FIELD_GROUPS_PATH) ?? [];
  const isNewFieldGroup = isUnsavedRow(fieldGroup, savedFieldGroups);
  // A saved group keeps its key, so matching on it is stable; a new group has no saved counterpart
  // and every one of its sub-fields is therefore removable.
  const savedFieldGroup = savedFieldGroups.find((saved) => saved?.id === fieldGroup.id);
  const savedSubFields = savedFieldGroup?.sub_fields ?? [];
  // The token is stored on the source row the key names, which any saved group naming the same key
  // was loaded with - including for a new group that names an existing key.
  const sourceKey = trim(fieldGroup.source?.source_key ?? '');
  const savedAuthToken = isEmpty(sourceKey)
    ? undefined
    : savedFieldGroups.find((saved) => saved?.source?.source_key === sourceKey)?.source?.auth_token;

  // Absent `active` counts as shown: the backend schema declares `active: Optional[bool] = True`.
  const isActive = fieldGroup.active !== false;
  const isStaticBacked = !isNil(get(values, `${fieldId}.source.static_json`));
  const subFields: HierarchySubField[] = get(values, `${fieldId}.sub_fields`) ?? [];
  const isEditingDisabled = disabled || !isActive;

  const incidentCategories = (get(values, 'configuration.categories', []) as { id: string; desc: string }[])?.map(
    (category) => ({ id: category.id, value: category.id, label: category.desc })
  );
  // Same grouped list the sibling field card offers - groups share the flat category namespace.
  const categories = buildCategoryLimitOptions(incidentCategories, policyCategoryOptions);

  // The Display Name -> Key sync stops once the user has really edited the Key, derived exactly as
  // the sibling field card derives it: the key is still tracking the name while it equals
  // snakeCase(desc), which holds by construction for a row from the factories above.
  const keyFollowsDesc = (path: string) => get(values, `${path}.id`, '') === snakeCase(get(values, `${path}.desc`, ''));

  const renderSubFieldRow = (
    subFieldIndex: number,
    arrayHelpers: FieldArrayRenderProps,
    dragHandleProps: DndDragHandleProps
  ) => {
    const subFieldPath = `${fieldId}.sub_fields[${subFieldIndex}]`;
    const subField: Partial<HierarchySubField> = get(values, subFieldPath, {});
    const isNewSubField = isUnsavedRow(subField, savedSubFields);
    const isRemovable = isNewSubField && subFields.length > MIN_SUB_FIELDS_PER_GROUP;

    return (
      <div className="grid grid-cols-12 items-center gap-12">
        <div className="col-span-5">
          <TextFieldFormik
            id={`${subFieldPath}.desc`}
            label={TEXTS.display_name_label}
            fullWidth
            disabled={isEditingDisabled}
            // Edit-driven key sync, not a value watcher: rows are keyed by position, so a reorder
            // shuffles data between positions and a watcher would read that as an edit.
            // @ts-expect-error - TextFieldFormik accepts onChange at runtime
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const newDesc = e.target.value;
              setFieldValue(`${subFieldPath}.desc`, newDesc);
              if (isNewSubField && keyFollowsDesc(subFieldPath)) {
                setFieldValue(`${subFieldPath}.id`, snakeCase(newDesc));
              }
            }}
          />
        </div>
        <div className="col-span-5">
          {isNewSubField ? (
            <TextFieldFormik id={`${subFieldPath}.id`} label={TEXTS.key_label} fullWidth disabled={isEditingDisabled} />
          ) : (
            <TextField label={TEXTS.key_label} value={subField.id ?? ''} fullWidth disabled />
          )}
        </div>
        {/* leading-[0] collapses the inline line-box so the ThreeDotsMenu's baseline-aligned icon
            centers with the flex-centered drag grip, as the sibling field rows do. */}
        <div className="col-span-2 flex items-center justify-end leading-[0]">
          <ReorderDragHandle dragHandleProps={dragHandleProps} disabled={isEditingDisabled} />
          <ThreeDotsMenu
            stopPropagation
            disabled={disabled || !isRemovable}
            options={[
              {
                onClick: () => arrayHelpers.remove(subFieldIndex),
                node: TEXTS.remove,
                key: 'remove',
                disabled: !isRemovable,
              },
            ]}
          />
        </div>
      </div>
    );
  };

  const renderAddSubFieldButton = (arrayHelpers: FieldArrayRenderProps) => {
    const atCap = subFields.length >= MAX_SUB_FIELDS_PER_GROUP;
    const addButton = (
      <Button
        variant="text"
        color="primary"
        onClick={() => arrayHelpers.push(newSubField(subFields.length + 1))}
        disabled={isEditingDisabled || atCap}
      >
        <AddIcon />
        {TEXTS.add_sub_field}
      </Button>
    );

    return atCap ? (
      <FsTooltip arrow title={TEXTS.add_sub_field_at_cap_tooltip.replace('{max}', String(MAX_SUB_FIELDS_PER_GROUP))}>
        {/* span keeps the tooltip working over a disabled button (which swallows pointer events) */}
        <span>{addButton}</span>
      </FsTooltip>
    ) : (
      addButton
    );
  };

  return (
    <CollapsibleWrapper
      title={fieldGroup.desc}
      subtitle={`${TEXTS.key_label}: ${fieldGroup.id ?? ''}`}
      backgroundColor={CollapsibleWrapper.BACKGROUND_COLOR.LIST}
      actionCard
      noBorderLine
      noDivider
      withActionsContainerFullWidth={false}
      defaultState={isNewFieldGroup}
      actions={
        <div className="flex items-center">
          {/* A static-backed group answers from a pasted tree, so say so on the collapsed card
              rather than only inside it - that is the state an admin needs to spot in a list. */}
          {isStaticBacked ? (
            <StatusChipCore status={TEXTS.static_indication} Icon={WarningCircleIcon} color="orange" />
          ) : null}
          <HiddenIndication isActive={isActive} />
          {actions}
        </div>
      }
    >
      <InnerCard>
        {/* A 12-column CSS grid, as the sub-field rows below already are: half-width fields take six
            columns and a full-width one takes twelve, and a row ends by itself when the next field
            no longer fits - so no spacer element stands in for an empty half. */}
        <div className="grid grid-cols-12 items-center gap-24">
          <div className="col-span-6">
            <TextFieldFormik
              id={`${fieldId}.desc`}
              label={TEXTS.display_name_label}
              fullWidth
              disabled={isEditingDisabled}
              // @ts-expect-error - TextFieldFormik accepts onChange at runtime
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newDesc = e.target.value;
                setFieldValue(`${fieldId}.desc`, newDesc);
                if (isNewFieldGroup && keyFollowsDesc(fieldId)) {
                  setFieldValue(`${fieldId}.id`, snakeCase(newDesc));
                }
              }}
            />
          </div>
          <div className="col-span-6">
            {isNewFieldGroup ? (
              <TextFieldFormik id={`${fieldId}.id`} label={TEXTS.key_label} fullWidth disabled={isEditingDisabled} />
            ) : (
              <TextField label={TEXTS.key_label} value={fieldGroup.id ?? ''} fullWidth disabled />
            )}
          </div>
          <div className="col-span-6">
            <SingleSelectField<FieldGroupType>
              label={TEXTS.type_label}
              value={fieldGroup.type}
              fullWidth
              disabled={isEditingDisabled || !isNewFieldGroup}
              onChange={(selectedType) => setFieldValue(`${fieldId}.type`, selectedType)}
            >
              {Object.entries(FIELD_GROUP_TYPE_LABELS).map(([type, label]) => (
                <MenuItem key={type} value={type}>
                  {label}
                </MenuItem>
              ))}
            </SingleSelectField>
          </div>
          <div className="col-span-6">
            <ArrayMultiselectFieldWithChipsFormik
              id={`${fieldId}.incident_sub_type_categories`}
              label={TEXTS.limit_to_categories_label}
              options={categories}
              disabled={isEditingDisabled || isEmpty(categories)}
            />
          </div>
          <div className="col-span-12">
            <SwitchFormik id={`${fieldId}.active`} label={TEXTS.show_label} hideErrorGap disabled={disabled} />
          </div>
          <div className="col-span-6">
            <CheckboxFormik id={`${fieldId}.mandatory`} label={TEXTS.required_label} disabled={isEditingDisabled} />
          </div>
          <div className="col-span-12">
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
              {TEXTS.source_title}
            </Text>
          </div>
          <div className="col-span-12">
            <FieldGroupSourceSummary
              fieldId={fieldId}
              savedAuthToken={savedAuthToken}
              isEditingDisabled={isEditingDisabled}
              onOpen={() => setIsSourceDialogOpen(true)}
            />
            {isSourceDialogOpen ? (
              <FieldGroupSourceDialog
                fieldGroupDesc={fieldGroup.desc ?? ''}
                source={fieldGroup.source ?? {}}
                // The summary shows a source error once its field is touched or a save was tried,
                // so the dialog's inputs open with the same fields revealed.
                sourceTouched={
                  submitCount > 0
                    ? setNestedObjectValues<FormikTouched<FieldGroupSource>>(fieldGroup.source ?? {}, true)
                    : (getIn(touched, `${fieldId}.source`) ?? {})
                }
                subFields={subFields}
                isNewFieldGroup={isNewFieldGroup}
                disabled={isEditingDisabled}
                onUpdate={async (source) => {
                  // An Update with nothing edited must not mark the form touched, or the screen would
                  // ask to discard changes that do not exist.
                  if (isEqual(source, fieldGroup.source)) {
                    setIsSourceDialogOpen(false);
                    return;
                  }
                  await setFieldValue(`${fieldId}.source`, source);
                  // Applying the dialog stands in for leaving the inline inputs it replaced, which
                  // marked their fields touched, so the screen's Cancel asks before dropping the edit.
                  Object.keys(source).forEach((key) => setFieldTouched(`${fieldId}.source.${key}`, true, false));
                  setIsSourceDialogOpen(false);
                }}
                onClose={() => setIsSourceDialogOpen(false)}
              />
            ) : null}
          </div>
          <div className="col-span-12">
            <FieldArray
              name={`${fieldId}.sub_fields`}
              render={(arrayHelpers) => (
                <div className="flex flex-col gap-12">
                  <div>
                    <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                      {TEXTS.sub_fields_title}
                    </Text>
                    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                      {TEXTS.sub_fields_note}
                    </Text>
                  </div>
                  <DndOrderedList
                    className="flex flex-col gap-12"
                    disabled={isEditingDisabled}
                    items={subFields.map((_, subFieldIndex) => ({ subFieldIndex }))}
                    // Key on the array position, not the sub-field's `id`: the Key tracks the
                    // Display Name while a row is new, so an id-derived key changes mid-edit and
                    // remounts the row, dropping input focus.
                    getItemId={({ subFieldIndex }) => String(subFieldIndex)}
                    onReorder={({ fromIndex, toIndex }) => reorderArrayField(arrayHelpers, fromIndex, toIndex)}
                    renderItem={({ subFieldIndex }, { dragHandleProps }) =>
                      renderSubFieldRow(subFieldIndex, arrayHelpers, dragHandleProps)
                    }
                  />
                  {/* The list-level rules (at least one level, unique level keys) have no single field
                      to sit on, so they are reported under the list, as the flat fields' are. */}
                  <ErrorHelperTextFormik id={`${fieldId}.sub_fields`} withoutChildren />
                  <div className="flex">{renderAddSubFieldButton(arrayHelpers)}</div>
                </div>
              )}
            />
          </div>
        </div>
      </InnerCard>
    </CollapsibleWrapper>
  );
};

export default FieldGroupConfigurationCard;
